import { FC, useContext, useEffect, useState } from "react";
import {
  Box,
  createStyles,
  Grid,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { Snackbar } from "@material-ui/core";
import { AccountCircleOutlined } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";

import { Button } from "components";
import { BaseContainer } from "containers";
import { AppContext } from "contexts";
import * as routes from "routes";
import { getPageURL } from "routes/config";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { getCurrentUser } from "services/users";
import { getProfileData, updateProfile } from "services/users";
import { UserProfile } from "services/users/types";
import theme from "theme";

import Form from "./Form";

const useStyles = makeStyles(() =>
  createStyles({
    profileContainer: {
      width: "100%",
      marginLeft: "auto",
      marginRight: "auto",
      maxWidth: "60rem",
    },
    card: {
      padding: theme.spacing(3),
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[3],
      backgroundColor: theme.palette.background.default,
    },
    profileIconSize: {
      fontSize: "3.5rem",
    },
    bold: {
      fontWeight: theme.typography.fontWeightMedium,
    },
    bigText: {
      fontSize: "1.8rem",
      paddingLeft: theme.spacing(1),
    },
    link: {
      fontSize: "0.93rem",
      fontWeight: 600,
      color: theme.custom.palette.softWhite.main,
      "&:hover": {
        color: theme.custom.palette.softWhite.main,
      },
    },
    spacingBottom: {
      paddingBottom: theme.spacing(4),
    },
    alignRight: {
      textAlign: "right",
    },
    mobileEmailInfo: {
      textAlign: "left",
      paddingBottom: theme.spacing(2),
    },
  }),
);

export interface FormValidation {
  open: boolean;
  status?: "error" | "success";
  message?: string;
}

const Profile: FC = ({}) => {
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { user, setUser, domainNames } = useContext(AppContext);
  const [userProfile, setUserProfile] = useState<UserProfile>();
  const [snackbarConfig, setSnackbarConfig] = useState<FormValidation>({
    open: false,
  });

  const handleSubmit = async (values: any) => {
    try {
      await updateProfile(values);
      setSnackbarConfig({
        open: true,
        status: "success",
        message: "Profile updated",
      });
      setUser(await getCurrentUser());
    } catch (e) {
      setSnackbarConfig({
        open: true,
        status: "error",
        message: DEFAULT_ERROR_MESSAGE,
      });
    }
  };

  useEffect(() => {
    const loadProfile = async () => {
      const userProfileData = await getProfileData();
      setUserProfile(userProfileData);
    };
    loadProfile();
  }, [setUserProfile]);

  return (
    <BaseContainer>
      <Box mt={5} className={classes.profileContainer}>
        <Box display="flex" alignItems="center" mb={1}>
          <AccountCircleOutlined className={classes.profileIconSize} />
          <Typography className={classes.bigText}>Profile</Typography>
        </Box>
        <Box className={classes.card}>
          <Grid container alignItems="flex-end">
            <Grid
              item
              xs={12}
              sm={8}
              className={`${isMobile ? classes.mobileEmailInfo : ""}`}
            >
              <Typography className={classes.bold}>Email</Typography>
              <Typography>{user.userEmail}</Typography>
            </Grid>
            <Grid item xs={12} sm={4} style={{ textAlign: "right" }}>
              <Button size="large" variant="contained" fullWidth={isMobile}>
                <a
                  className={classes.link}
                  href={getPageURL(
                    routes.directoryChangePassword(),
                    domainNames,
                  )}
                >
                  Change Your Password
                </a>
              </Button>
            </Grid>
          </Grid>
          {userProfile && (
            <Form initialValues={userProfile} onSubmit={handleSubmit}></Form>
          )}
        </Box>
      </Box>
      <Snackbar
        open={snackbarConfig.open}
        autoHideDuration={3000}
        onClose={() => setSnackbarConfig({ open: false })}
      >
        <Alert
          onClose={() => setSnackbarConfig({ open: false })}
          severity={snackbarConfig.status}
        >
          {snackbarConfig.message}
        </Alert>
      </Snackbar>
    </BaseContainer>
  );
};

export default Profile;
